import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getServiceList } from "apis";
import { SearchBar, Loading } from "components";
import { ServiceCategory } from "types";
import { ServiceDTO } from "types/DTOs/service/service.dto";
import { getImageUrl } from "utils/image.util";

const categoryList: { label: string; value: ServiceCategory | undefined }[] = [
  { label: "전체", value: undefined },
  { label: "💬 수다·티타임", value: ServiceCategory.CHAT },
  { label: "🎮 게임·듀오", value: ServiceCategory.GAME },
  { label: "🔮 타로·운세", value: ServiceCategory.TAROT },
  { label: "💡 고민·피드백", value: ServiceCategory.FEEDBACK },
  { label: "💻 랜선 스터디", value: ServiceCategory.STUDY },
  { label: "✨ 자유주제", value: ServiceCategory.FREE },
];

const getCategoryEmoji = (category: ServiceCategory): string => {
  switch (category) {
    case ServiceCategory.CHAT: return "💬";
    case ServiceCategory.GAME: return "🎮";
    case ServiceCategory.TAROT: return "🔮";
    case ServiceCategory.FEEDBACK: return "💡";
    case ServiceCategory.STUDY: return "💻";
    case ServiceCategory.FREE: return "✨";
    default: return "✨";
  }
};

const getCategoryColor = (category: ServiceCategory): string => {
  switch (category) {
    case ServiceCategory.CHAT: return "bg-pink-50 text-pink-600";
    case ServiceCategory.GAME: return "bg-purple-50 text-purple-600";
    case ServiceCategory.TAROT: return "bg-violet-50 text-violet-600";
    case ServiceCategory.FEEDBACK: return "bg-amber-50 text-amber-600";
    case ServiceCategory.STUDY: return "bg-blue-50 text-blue-600";
    case ServiceCategory.FREE: return "bg-emerald-50 text-emerald-600";
    default: return "bg-achromatic-100 text-achromatic-500";
  }
};

const ServiceList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') as ServiceCategory | null;

  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | undefined>(
    initialCategory || undefined
  );

  const { data: servicesData, isLoading, isError } = useQuery(
    ["services", selectedCategory],
    () => getServiceList(selectedCategory)
  );

  const rawServices: ServiceDTO[] = servicesData?.data?.data ?? [];
  const services: ServiceDTO[] = rawServices.filter((service) => {
    const availableSlots = service.schedules?.filter((s) => {
      if (s.isBooked || !s.date) return false;
      const timeStr = s.endTime || s.startTime || "23:59:59";
      const slotDate = new Date(`${s.date}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}`);
      return !isNaN(slotDate.getTime()) && slotDate.getTime() > Date.now();
    }).length ?? 0;
    return availableSlots > 0;
  });

  return (
    <div className="animate-fadeUp">
      <h1 className="text-xl font-bold text-achromatic-900 mb-4">서비스</h1>

      {/* Search */}
      <SearchBar onSearch={() => {}} />

      {/* Category filter */}
      <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1">
        {categoryList.map((cat) => (
          <button
            key={cat.label}
            onClick={() => setSelectedCategory(cat.value)}
            className={`whitespace-nowrap px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all ${
              selectedCategory === cat.value
                ? "bg-achromatic-900 text-white"
                : "bg-white text-achromatic-500 hover:text-achromatic-700 hover:bg-achromatic-50"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Service list */}
      {isLoading && <Loading />}
      {isError && (
        <div className="bg-white rounded-2xl p-10 text-center text-achromatic-400 text-sm">
          서비스를 불러오는 중 오류가 발생했습니다
        </div>
      )}
      {!isLoading && !isError && services.length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center">
          <span className="text-4xl block mb-3">📭</span>
          <p className="text-sm font-semibold text-achromatic-800 mb-1">등록된 서비스가 없습니다</p>
          <p className="text-xs text-achromatic-400">판매 가능 시간이 설정된 서비스만 리스트에 표시됩니다</p>
        </div>
      )}

      {/* Card grid */}
      <div className="flex flex-col gap-3">
        {services.map((service) => {
          const availableSlots = service.schedules?.filter((s) => {
            if (s.isBooked || !s.date) return false;
            const timeStr = s.endTime || s.startTime || "23:59:59";
            const slotDate = new Date(`${s.date}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}`);
            return !isNaN(slotDate.getTime()) && slotDate.getTime() > Date.now();
          }).length ?? 0;
          const mainImage = service.images && service.images.length > 0 ? service.images[0] : null;

          return (
            <div
              key={service.id}
              className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-card-hover transition-all duration-200 group border border-achromatic-50"
              onClick={() => navigate(`/service/${service.id}`)}
            >
              <div className="flex mobile:flex-col">
                {/* Image */}
                <div className="w-[200px] h-[160px] flex-shrink-0 bg-achromatic-50 mobile:w-full mobile:h-[180px]">
                  {mainImage ? (
                    <img
                      src={mainImage.startsWith('http') ? mainImage : `${process.env.REACT_APP_API_URL}/${mainImage}`}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-achromatic-50 to-achromatic-100">
                      {getCategoryEmoji(service.category)}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${getCategoryColor(service.category)}`}>
                        {getCategoryEmoji(service.category)} {service.category}
                      </span>
                      <span className={`text-[11px] font-semibold ${
                        availableSlots > 0 ? "text-primary-500" : "text-achromatic-300"
                      }`}>
                        {availableSlots > 0 ? `${availableSlots}개 가능` : "마감"}
                      </span>
                    </div>
                    <h3 className="text-[16px] font-bold text-achromatic-900 mb-1.5 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[13px] text-achromatic-400 line-clamp-2 leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  <div className="flex justify-between items-center pt-3 mt-3 border-t border-achromatic-50">
                    <div className="flex items-center gap-2">
                      {service.host?.profileImage ? (
                        <img
                          src={getImageUrl(service.host.profileImage)}
                          alt="host"
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-achromatic-100 rounded-full flex items-center justify-center text-[10px] font-bold text-achromatic-500">
                          {service.host?.name?.charAt(0) ?? "?"}
                        </div>
                      )}
                      <span className="text-[12px] text-achromatic-500">{service.host?.name ?? "호스트"}</span>
                      <span className="text-[10px] text-achromatic-300">·</span>
                      <span className="text-[11px] text-achromatic-300">{service.durationMinutes}분</span>
                    </div>
                    <div>
                      <span className="text-[17px] font-extrabold text-achromatic-900">{service.price.toLocaleString()}</span>
                      <span className="text-[12px] text-achromatic-400 ml-0.5">원</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceList;
